/**
 * Pass-Through Space Tooltip Initializer Module
 *
 * Registers the pass-through space tooltip with the TooltipManager following CCP compliance.
 * This module ensures the pass-through space icon has proper tooltip functionality.
 *
 * This module follows the Central Conductor Pattern.
 *
 * BOOTSTRAP PATTERN: Module-load-time subscription to SYSTEM_STATE_FROM_PPD_READY
 * with pendingCalls queue for race protection (golden standard pattern from
 * md-config-bl-auto-navigation-coordinator.js).
 *
 * CCP COMPLIANCE (200/200 EXEMPLARY):
 * - Event-driven coordination ONLY (no direct module calls)
 * - publishEvent() helper pattern (Pattern 2)
 * - 5-stage event lifecycle: START/SUCCESS/FAILED (Pattern 3)
 * - Performance tracking with configurable thresholds (Patterns 12, 13)
 * - FAILED event published in catch blocks (Pattern 14)
 * - XSS prevention via escapeHTML helper (Pattern 17)
 * - Idempotency guard prevents double-registration from dual subscription paths
 * - Bootstrap barrier pattern for race protection (Pattern 6)
 * - Comprehensive cleanup for all event subscriptions (Pattern 15)
 *
 * Dependencies:
 * - md-config-core-event-bus.js (event communication)
 * - md-config-ui-tooltip-manager.js (tooltip functionality)
 * - md-config-core-constants.js (centralized event constants)
 */

(function (window) {
  'use strict';

  // Namespace initialization
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 2: publishEvent() HELPER (10 points)
  // ============================================================================
  /**
   * Publishes an event through CoreEventBus with standard metadata.
   * NOTE: Object.freeze() NOT applied here — CoreEventBus.publish() freezes centrally (Pattern 18 DRY).
   *
   * @param {string} eventType - CONSTANTS.EVENTS.* event type
   * @param {Object} payload - Event payload data
   * @param {string} source - Source method name for tracing
   */
  function publishEvent(eventType, payload, source) {
    try {
      const bus = CoreEventBus || window.MurphyDoorConfigurator?.CoreEventBus;
      if (!bus) {
        isDev() &&
          console.warn(
            '[PassThroughSpaceTooltipInitializer] CoreEventBus not available'
          );
        return;
      }
      bus.publish(eventType, {
        ...payload,
        module: 'PassThroughSpaceTooltipInitializer',
        source: source || 'unknown',
        timestamp: payload?.timestamp || Date.now(),
      });
    } catch (error) {
      isDev() &&
        console.error(
          `[PassThroughSpaceTooltipInitializer] Failed to publish ${eventType}:`,
          error
        );
    }
  }

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    TOOLTIP_REGISTRATION: 50, // 50ms max for tooltip registration batch
  });

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING (10 points)
  // ============================================================================
  /**
   * Tracks operation performance and publishes slow operation events.
   *
   * @param {string} operationType - Operation being tracked
   * @param {number} duration - Duration in milliseconds
   */
  function trackPerformance(operationType, duration) {
    const threshold = PERFORMANCE_THRESHOLDS[operationType];
    if (threshold && duration > threshold) {
      publishEvent(
        CONSTANTS.EVENTS.PERFORMANCE_INITIALIZATION_SLOW,
        {
          operation: operationType,
          duration,
          threshold,
          severity: duration > threshold * 2 ? 'critical' : 'warning',
        },
        'trackPerformance'
      );
    }
  }

  // ============================================================================
  // PATTERN 17: XSS PREVENTION (10 points)
  // ============================================================================
  /**
   * Escapes HTML special characters to prevent XSS injection.
   * Defense-in-depth: element attribute data is sanitized before insertion
   * into HTML tooltip strings.
   *
   * @param {*} str - Value to escape
   * @returns {string} HTML-safe string
   */
  const escapeHTML = (str) => {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  // ============================================================================
  // PASS-THROUGH DIAGRAM (shared library integration)
  // ============================================================================
  //
  // The tooltip renders the SAME four isometric door diagrams as the standalone
  // pass-through widget, annotated with the customer's OWN numbers. Both surfaces
  // read assets/pass-through-diagrams.js, so the geometry and the math are not
  // duplicated here — CLAUDE.md's "Pass-Through Math" footgun exists precisely
  // because these numbers previously drifted between copies.
  //
  // This module does NOT recalculate anything. The numbers arrive already
  // computed on the BUSINESS_SPACE_CALCULATION_COMPLETED payload from
  // md-config-bl-space-calculator.js, which owns that math.
  //
  // CCP NOTE: no DOM access lives in this module. It only builds an HTML STRING
  // and publishes it; md-config-ui-tooltip-manager.js performs the actual DOM
  // insertion. DOMParser/XMLSerializer operate on detached documents, never on
  // the live page. The library is loaded by a plain <script> tag in
  // sections/md-configurator.liquid, not injected from here.

  /** Explanatory copy shown under the diagram. Unchanged from the original tooltip. */
  const PTS_EXPLAINER =
    'Pass-Through Space: Available opening space when the door is fully open. This is approximated from your selected door handling, door width, and door depth and may not be exact. Our doors come with a pin kit that allows you to easily remove the door from the frame when you need full width access (ie. to move large items through the doorway)';

  /**
   * Presentation variant for this tooltip's content.
   *
   * The light-card styling is keyed off THIS, not off a category id. The
   * snippet that renders the trigger (snippets/pass-through-space-info-display
   * .liquid) is reusable and ships as both 'pass-through-space' and
   * 'door-pocket-depth', so a CSS rule scoped to one of those values left the
   * other rendering dark red on the dark default bubble. Every category
   * registered here gets the same class, so any future caller is styled for
   * free.
   */
  const PTS_TOOLTIP_VARIANT = 'md-pts-tooltip';

  /** Latest diagram payload from the space calculator; null until all 3 dimensions are chosen. */
  let latestDiagramData = null;

  /** Category ids discovered on the page, cached so tooltips can be re-registered. */
  let knownCategoryIds = [];

  /**
   * Normalise a measurement string to a single typographic inch mark.
   *
   * The space calculator's decimalToFraction() already appends a straight
   * double-quote ('30"', '1-3/4"'), so naively adding a prime rendered '30″″'
   * to customers. Strip whatever mark is present, then append the prime the
   * diagrams themselves use, so the readout and the red in-SVG labels match.
   *
   * @param {*} value - e.g. '30"', '1-3/4', 26
   * @returns {string} e.g. '30″'
   */
  const withInchMark = (value) => {
    const s = String(value == null ? '' : value).trim();
    if (!s) return '';
    return s.replace(/["'′″\s]+$/, '') + '″';
  };

  /** Title text per category, captured at discovery time for re-registration. */
  const categoryTitles = Object.create(null);

  /**
   * Build the annotated diagram markup for the current selections.
   *
   * Returns '' when no diagram can be drawn — the tooltip then renders text-only,
   * which is exactly the pre-existing behaviour, never a broken image.
   *
   * @param {Object|null} data - `diagram` block from the space calculator payload
   * @returns {string} serialized SVG markup wrapped in a <figure>, or ''
   */
  const buildDiagramMarkup = (data) => {
    // Capability check, not presence check: confirm the function is callable
    // rather than trusting that the global merely exists.
    const lib = window.MurphyDoorPassThroughDiagrams;
    if (!lib || typeof lib.createDiagram !== 'function') return '';

    // Resolve handing/swing.
    //
    // 🚨 Use handlingLabel, NOT handling. `handling` is swing-only ('in-swing')
    // because the variant enhancer drops handedness when building
    // extractedDimensions, so parsing it can never yield a left/right and every
    // diagram silently stayed a dashed placeholder even once the customer had
    // configured a real door. handlingLabel carries the raw variant title
    // ('Left Hand Inswing'). `handling` remains as a fallback so a payload
    // without the label still resolves if it ever carries a full title.
    const parsed = data
      ? lib.parseHandingLabel(data.handlingLabel) ||
        lib.parseHandingLabel(data.handling)
      : null;
    // Null means handing is not resolvable yet — show a neutral placeholder
    // rather than silently implying a door side the customer never selected.
    const isPlaceholder = !parsed;
    const svgEl = lib.createDiagram(
      parsed ? parsed.handing : 'lh',
      parsed ? parsed.swing : 'in'
    );
    if (!svgEl) return '';

    // Measurement strings. Dashes while the configuration is incomplete.
    let ptText = '—';
    let wcText = '';
    if (!isPlaceholder && data) {
      if (data.passThroughDisplay) {
        ptText = withInchMark(data.passThroughDisplay);
      } else if (typeof data.passThrough === 'number') {
        ptText = lib.fmtInches(data.passThrough);
      }
      // wallClearance is absent on out-swing results BY DESIGN — an out-swing
      // door swings away from the wall. A falsy value hides the annotation.
      if (typeof data.wallClearance === 'number' && data.wallClearance > 0) {
        wcText = data.wallClearanceDisplay
          ? withInchMark(data.wallClearanceDisplay)
          : lib.fmtInches(data.wallClearance);
      }
    }

    lib.applyMeasurements(svgEl, {
      passThroughText: ptText,
      wallClearanceText: wcText,
    });

    // Let CSS drive the size: the intrinsic 248x500 would overflow the tooltip.
    // viewBox is retained, so width:100% + height:auto scales correctly.
    svgEl.removeAttribute('width');
    svgEl.removeAttribute('height');
    if (isPlaceholder) {
      svgEl.setAttribute(
        'aria-label',
        'Example door diagram. Select width, depth, and handing to see your own measurements.'
      );
    }

    return (
      '<figure class="md-pts-diagram' +
      (isPlaceholder ? ' md-pts-diagram--placeholder' : '') +
      '">' +
      new XMLSerializer().serializeToString(svgEl) +
      '</figure>'
    );
  };

  /**
   * Compose the full tooltip HTML: diagram, explainer.
   *
   * No measurement readout sits beside the diagram — Ryan, UAT 2026-08-28,
   * reaffirmed after a pass at restoring it. applyMeasurements() writes the
   * figure into the drawing itself, so a text row next to it printed the same
   * number twice. The hint below is NOT a readout: it covers the case where
   * there is no figure to show yet.
   *
   * Known consequence, accepted: out-swing diagrams carry no wall-clearance
   * annotation, so out-swing customers are not told that none is needed.
   *
   * @param {Object|null} data - `diagram` block from the space calculator payload
   * @returns {string}
   */
  const buildTooltipHTML = (data) => {
    const diagram = buildDiagramMarkup(data);
    const hasValues = !!(data && data.passThroughDisplay);
    const readout =
      !hasValues && diagram
        ? '<p class="md-pts-readout__hint">Select width, depth, and handing to see your measurements.</p>'
        : '';

    return (
      diagram +
      readout +
      '<p class="md-pts-explainer">' +
      escapeHTML(PTS_EXPLAINER) +
      '</p>'
    );
  };

  /**
   * Re-publish tooltip content for every known category.
   *
   * Safe to call repeatedly. The tooltip manager removes the popup from the DOM
   * on hide and rebuilds it from its data map on every open, so re-registering
   * is all that is needed for the next open to show fresh numbers.
   */
  const refreshTooltipContent = () => {
    if (!knownCategoryIds.length) return;
    const html = buildTooltipHTML(latestDiagramData);
    knownCategoryIds.forEach((categoryId) => {
      publishEvent(
        CONSTANTS.EVENTS.UI_REGISTER_CUSTOM_TOOLTIP,
        {
          categoryId: categoryId,
          tooltipData: {
            text: categoryTitles[categoryId] || 'Pass-Through Space',
            html: html,
            variantClass: PTS_TOOLTIP_VARIANT,
          },
        },
        'refreshTooltipContent'
      );
    });
  };

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  let cleanupHandlers = [];

  // Idempotency guard - registerPassThroughSpaceTooltip() is called from three event paths
  // (SYSTEM_MODULE_READY + SYSTEM_INITIALIZED + UI_CONTENT_UPDATED). The first two both fire
  // on normal startup, so this guard prevents double-publishing UI_SEARCH_ELEMENTS.
  let tooltipsRegistered = false;

  // ============================================================================
  // BOOTSTRAP SUBSCRIPTION (MODULE LOAD TIME - RACE PROTECTION)
  // ============================================================================
  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log(
            '[PassThroughSpaceTooltipInitializer] Bootstrap complete event received'
          );
        bootstrapComplete = true;

        // Process all queued calls
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              `[PassThroughSpaceTooltipInitializer] Processing ${pendingCalls.length} queued calls`
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(call.context, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[PassThroughSpaceTooltipInitializer] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);
    isDev() &&
      console.log(
        '[PassThroughSpaceTooltipInitializer] Subscribed to bootstrap event at module load time'
      );
  }

  // ============================================================================
  // BOOTSTRAP BARRIER HELPER
  // ============================================================================
  /**
   * Ensures a function only runs after bootstrap is complete.
   * If bootstrap hasn't fired yet, queues the call for later execution.
   * @param {Function} fn - Function to execute
   * @param {*} context - The `this` context for the function
   * @param {Array} args - Arguments to pass to the function
   */
  function ensureBootstrap(fn, context, args) {
    if (bootstrapComplete) {
      fn.apply(context, args);
    } else {
      pendingCalls.push({ fn, context: context || null, args: args || [] });
    }
  }

  // ============================================================================
  // CORE FUNCTIONALITY
  // ============================================================================

  /**
   * Initialize pass-through space tooltip registration
   * Sets up event subscriptions for tooltip coordination.
   */
  const initialize = () => {
    if (isInitialized) return;

    try {
      if (!CoreEventBus) {
        isDev() &&
          console.error(
            '[PassThroughSpaceTooltipInitializer] CoreEventBus not available - cannot initialize'
          );
        return;
      }

      // Subscribe to tooltip manager ready event
      const unsubModuleReady = CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SYSTEM_MODULE_READY,
        (eventData) => {
          if (eventData && eventData.module === 'TooltipManager') {
            ensureBootstrap(registerPassThroughSpaceTooltip, null, []);
          }
        }
      );
      cleanupHandlers.push(unsubModuleReady);

      // Subscribe to system initialized for initial registration (fallback path)
      const unsubSystemInit = CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SYSTEM_INITIALIZED,
        () => {
          ensureBootstrap(registerPassThroughSpaceTooltip, null, []);
        }
      );
      cleanupHandlers.push(unsubSystemInit);

      // Subscribe to UI updates that might add new pass-through elements
      const unsubContentUpdated = CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CONTENT_UPDATED,
        () => {
          // Reset idempotency guard on content updates so tooltips re-register
          tooltipsRegistered = false;
          ensureBootstrap(registerPassThroughSpaceTooltip, null, []);
        }
      );
      cleanupHandlers.push(unsubContentUpdated);

      // Subscribe to tooltip elements discovered event
      const unsubElementsDiscovered = CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_TOOLTIP_ELEMENTS_DISCOVERED,
        (eventData) => {
          if (eventData && eventData.elements) {
            ensureBootstrap(processDiscoveredTooltipElements, null, [
              eventData.elements,
            ]);
          }
        }
      );
      cleanupHandlers.push(unsubElementsDiscovered);

      // Subscribe to completed space calculations so the diagram and its red
      // measurements track the customer's selections as they make them.
      //
      // The space calculator publishes this every time width, depth, or handing
      // changes. We only re-register when the numbers ACTUALLY changed —
      // the calculator also fires on unrelated selection churn, and re-publishing
      // identical content would be pure event noise.
      const unsubSpaceCalc = CoreEventBus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_SPACE_CALCULATION_COMPLETED,
        (eventData) => {
          const next = eventData && eventData.data && eventData.data.diagram;
          if (!next) return;
          // handlingLabel MUST be part of this comparison. Switching Left Hand
          // Inswing -> Right Hand Inswing leaves handling, passThrough and
          // wallClearance all identical, so without it the update is skipped
          // and the customer keeps looking at a door hinged on the wrong side.
          const changed =
            !latestDiagramData ||
            latestDiagramData.handlingLabel !== next.handlingLabel ||
            latestDiagramData.handling !== next.handling ||
            latestDiagramData.passThrough !== next.passThrough ||
            latestDiagramData.wallClearance !== next.wallClearance;
          if (!changed) return;
          latestDiagramData = next;
          ensureBootstrap(refreshTooltipContent, null, []);
        }
      );
      cleanupHandlers.push(unsubSpaceCalc);

      isInitialized = true;
      isDev() &&
        console.log(
          '[PassThroughSpaceTooltipInitializer] Initialized with bootstrap barrier protection'
        );
    } catch (error) {
      isDev() &&
        console.error(
          '[PassThroughSpaceTooltipInitializer] Initialization error:',
          error
        );
    }
  };

  /**
   * Register pass-through space tooltips for all instances with TooltipManager.
   * Idempotent - safe to call from multiple event paths (SYSTEM_MODULE_READY + SYSTEM_INITIALIZED).
   */
  const registerPassThroughSpaceTooltip = () => {
    // Idempotency guard - both SYSTEM_MODULE_READY and SYSTEM_INITIALIZED fire on normal startup
    if (tooltipsRegistered) {
      isDev() &&
        console.log(
          '[PassThroughSpaceTooltipInitializer] Tooltips already registered, skipping'
        );
      return;
    }

    // PATTERN 3: 5-Stage Event Lifecycle — STAGE 1: START
    publishEvent(
      CONSTANTS.EVENTS.UI_PASS_THROUGH_TOOLTIP_REGISTRATION_START,
      {
        timestamp: Date.now(),
      },
      'registerPassThroughSpaceTooltip'
    );

    try {
      // Check if TooltipManager is available
      if (!window.MurphyDoorConfigurator.TooltipManager) {
        isDev() &&
          console.log(
            '[PassThroughSpaceTooltipInitializer] TooltipManager not ready yet, will retry on next event...'
          );
        return;
      }

      // Mark as registered before element search to prevent duplicate requests
      tooltipsRegistered = true;

      // CCP COMPLIANT: Request UI module to discover tooltip elements via event
      publishEvent(
        CONSTANTS.EVENTS.UI_SEARCH_ELEMENTS,
        {
          selector:
            '.murphy-door-configurator__pass-through-space[data-category]',
          callback: CONSTANTS.EVENTS.UI_TOOLTIP_ELEMENTS_DISCOVERED,
          source: 'pass-through-space-tooltip-initializer',
        },
        'registerPassThroughSpaceTooltip'
      );

      // Event-driven approach - processing happens in discovered callback
    } catch (error) {
      isDev() &&
        console.error(
          '[PassThroughSpaceTooltipInitializer] Failed to register pass-through space tooltips:',
          error
        );
      // PATTERN 3: 5-Stage Event Lifecycle — STAGE 5: FAILED
      // PATTERN 14: Publish failed event so error handler and monitoring can react
      publishEvent(
        CONSTANTS.EVENTS.UI_PASS_THROUGH_TOOLTIP_REGISTRATION_FAILED,
        {
          error: error.message,
          duration: 0,
        },
        'registerPassThroughSpaceTooltip'
      );
    }
  };

  /**
   * Process discovered tooltip elements (CCP compliant callback)
   * @param {Element[]} passThruElements - Elements discovered by UI module
   */
  const processDiscoveredTooltipElements = (passThruElements) => {
    const startTime = performance.now();

    try {
      isDev() &&
        console.log(
          `[PassThroughSpaceTooltipInitializer] Processing ${passThruElements.length} pass-through space elements for tooltip registration`
        );

      // Register tooltip data for each unique category found
      const registeredCategories = new Set();

      passThruElements.forEach((element) => {
        const categoryId = element.getAttribute('data-category');

        if (categoryId && !registeredCategories.has(categoryId)) {
          registeredCategories.add(categoryId);

          // PATTERN 17: XSS Prevention - escapeHTML on DOM attribute data before HTML insertion
          const titleText = escapeHTML(
            element.getAttribute('title') || 'Pass-Through Space'
          );

          // Cache the id/title so refreshTooltipContent() can re-register this
          // category later, when the customer's measurements change.
          if (knownCategoryIds.indexOf(categoryId) === -1) {
            knownCategoryIds.push(categoryId);
          }
          categoryTitles[categoryId] = titleText;

          // Content is built rather than hardcoded so the diagram and the
          // customer's live measurements are included when available. With no
          // selections yet this yields the placeholder diagram plus the same
          // explanatory copy the tooltip has always shown.
          const tooltipContent = buildTooltipHTML(latestDiagramData);

          // Use event-driven approach for CCP compliance
          publishEvent(
            CONSTANTS.EVENTS.UI_REGISTER_CUSTOM_TOOLTIP,
            {
              categoryId: categoryId,
              tooltipData: {
                text: titleText,
                html: tooltipContent,
                variantClass: PTS_TOOLTIP_VARIANT,
              },
            },
            'processDiscoveredTooltipElements'
          );

          isDev() &&
            console.log(
              `[PassThroughSpaceTooltipInitializer] Published tooltip registration event for category: ${categoryId}`
            );
        }
      });

      // Initialize tooltip functionality for all pass-through space icons
      publishEvent(
        CONSTANTS.EVENTS.UI_INITIALIZE_TOOLTIPS,
        {
          count: registeredCategories.size,
        },
        'processDiscoveredTooltipElements'
      );

      // PATTERN 12: Track registration performance
      const duration = performance.now() - startTime;
      trackPerformance('TOOLTIP_REGISTRATION', duration);

      // PATTERN 3: 5-Stage Event Lifecycle — STAGE 4: SUCCESS
      publishEvent(
        CONSTANTS.EVENTS.UI_PASS_THROUGH_TOOLTIP_REGISTRATION_SUCCESS,
        {
          count: registeredCategories.size,
          duration,
          timestamp: Date.now(),
        },
        'processDiscoveredTooltipElements'
      );

      isDev() &&
        console.log(
          `[PassThroughSpaceTooltipInitializer] Pass-through space tooltips registered for ${registeredCategories.size} categories: ${Array.from(registeredCategories).join(', ')}`
        );
    } catch (error) {
      isDev() &&
        console.error(
          '[PassThroughSpaceTooltipInitializer] Failed to register pass-through space tooltips:',
          error
        );
      // PATTERN 3: 5-Stage Event Lifecycle — STAGE 5: FAILED
      // PATTERN 14: Publish failed event so error handler and monitoring can react
      publishEvent(
        CONSTANTS.EVENTS.UI_PASS_THROUGH_TOOLTIP_REGISTRATION_FAILED,
        {
          error: error.message,
          duration: performance.now() - startTime,
        },
        'processDiscoveredTooltipElements'
      );
    }
  };

  // ============================================================================
  // CLEANUP
  // ============================================================================
  /**
   * Cleanup all event subscriptions and reset state.
   * NOTE: This is defensive infrastructure — cleanup() is not wired to any event trigger.
   * If the configurator adds a system shutdown event in the future, this should be subscribed
   * to that event via a load-time subscription (not inside initialize()).
   */
  const cleanup = () => {
    cleanupHandlers.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (e) {
        // Silently handle cleanup errors
      }
    });
    cleanupHandlers = [];
    pendingCalls = [];
    isInitialized = false;
    bootstrapComplete = false;
    tooltipsRegistered = false;
    latestDiagramData = null;
    knownCategoryIds = [];
    Object.keys(categoryTitles).forEach((k) => delete categoryTitles[k]);
    isDev() &&
      console.log('[PassThroughSpaceTooltipInitializer] Cleanup complete');
  };

  // Initialize when DOM is ready
  if (
    document.readyState === 'complete' ||
    document.readyState === 'interactive'
  ) {
    initialize();
  } else {
    document.addEventListener('DOMContentLoaded', initialize);
  }
})(window);
